import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { DashboardLayout } from "../../../components/layout";
import FreightAdminTableToolbar from "../../../components/admin/FreightAdminTableToolbar";
import FreightFaqDtActions from "../../../components/admin/FreightFaqDtActions";
import { AdminDataTable } from "../../../components/datatable";
import { FREIGHT_FAQ_COLUMNS } from "../../../config/freightFaqAdminColumns";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import { useFreightAdminTablePrefs } from "../../../hooks/useFreightAdminTablePrefs";
import { changeFreightFaqStatus, getFreightFaqIndex } from "../../../utils/api";
import { freightAdminListFromResponse } from "../../../utils/freightAdminListResponse";
import { showError, showSuccess } from "../../../utils/toast";
import "./adminFreight.css";

const TABLE_ID = "freight_faqs";
const COLUMN_DEFS = FREIGHT_FAQ_COLUMNS;

const sortRows = (list) =>
  [...list].sort((a, b) => {
    const sec = String(a.section_name || "").localeCompare(String(b.section_name || ""));
    if (sec !== 0) return sec;
    const o = Number(a.order_no) - Number(b.order_no);
    if (o !== 0) return o;
    return String(a.question || "").localeCompare(String(b.question || ""));
  });

const FreightFaqsPage = () => {
  const navigate = useNavigate();
  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = useMemo(
    () => [
      { label: roleLabel, link: "/dashboard" },
      { label: "Freight", link: "/freight" },
      { label: "FAQs" }
    ],
    [roleLabel]
  );

  const columnDefs = useMemo(() => COLUMN_DEFS, []);
  const {
    visibility,
    toggleColumn,
    resetColumnsVisible,
    resetColumnOrder,
    orderedColumnDefs,
    onColumnReorder
  } = useFreightAdminTablePrefs(TABLE_ID, columnDefs);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFreightFaqIndex();
      setRows(sortRows(freightAdminListFromResponse(res)));
    } catch (e) {
      showError(e.message || "Failed to load FAQs");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const getNavigate = useCallback(() => navigate, [navigate]);

  const handleToggleStatus = useCallback(
    async (row) => {
      if (!row?.id) return;
      const ok = window.confirm(String(row.status) === "1" ? "Deactivate this FAQ?" : "Activate this FAQ?");
      if (!ok) return;
      try {
        const res = await changeFreightFaqStatus({ id: row.id });
        showSuccess(res?.message || "Status updated");
        await load();
      } catch (e) {
        showError(e.message || "Update failed");
      }
    },
    [load]
  );

  const renderActions = useCallback(
    (row) => <FreightFaqDtActions row={row} getNavigate={getNavigate} onToggleStatus={handleToggleStatus} />,
    [getNavigate, handleToggleStatus]
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [r.section_name, r.question, r.answer, r.order_no, r.status, r.id]
        .map((x) => String(x ?? "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [rows, search]);

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="max-w-[1400px]">
        <FreightAdminTableToolbar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Filter by section, question, answer…"
          columnDefs={orderedColumnDefs}
          visibility={visibility}
          onToggleColumn={toggleColumn}
          onResetColumns={resetColumnsVisible}
          onResetColumnOrder={resetColumnOrder}
          rightSlot={
            <Link to="/freight/faqs/new" className="btn-primary no-underline inline-block">
              + New FAQ
            </Link>
          }
        />

        <div className="card offers-table border-l-4 border-l-[#064E8D]">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-2">
            <h3 className="m-0">Freight FAQs ({filteredRows.length})</h3>
            <button type="button" className="btn-outline text-sm" onClick={() => load()}>
              Refresh
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            Questions and answers shown in the freight app, grouped by section. Manage sections under{" "}
            <Link to="/freight/faq_section" className="text-[#064E8D] underline">
              FAQ Section
            </Link>
            .
          </p>

          <AdminDataTable
            columnDefs={orderedColumnDefs}
            visibility={visibility}
            rows={filteredRows}
            loading={loading}
            emptyText="No FAQs yet."
            renderActions={renderActions}
            onColumnReorder={onColumnReorder}
          />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FreightFaqsPage;
